import {
  BrandImpactData,
  BusinessImpactData,
  GroupByOptionsBrandEquityMetric,
  RevenueImpactTakeaways
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import {
  CompanyFiscalCal,
  DateGroupingKey,
  getGroupingValue
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { DateGroupingOption } from '@src/utils/date-utils';
import { isQueryVolumeMetric } from './metricNormalization';

export const removeDashPFromEndOfMetric = (metric: string): string => {
  const cleanedMetric = metric.endsWith('_p')
    ? metric
        .slice(0, -2)
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : metric
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  return cleanedMetric;
};

export const hasClientFacingOptions = (
  data: Record<string, GroupByOptionsBrandEquityMetric[]>
): boolean => Object.values(data).some(array => array.some(item => item.client_facing === true));

export const hasClientFacingItem = (data: GroupByOptionsBrandEquityMetric[]): boolean =>
  Array.isArray(data) && data.some(item => item.client_facing === true);

export const getMostRecentDateAndKPI = (
  data: Record<
    string,
    { value: string; client_facing: boolean; branch: string; stage_one: boolean }[]
  >
): { date: string; kpi: string } => {
  let defaultDate = 'latest';
  let value;
  const [firstLatest] = data.latest ?? [];
  if (firstLatest?.value) {
    ({ value } = firstLatest);
  }

  if (Object.hasOwn(data, 'latest') && data.latest.some(item => item.client_facing === true)) {
    defaultDate = 'latest';
    value = data.latest.find(item => item.client_facing === true)?.value || '';
  } else {
    const dates = Object.keys(data).filter(key => !Number.isNaN(Date.parse(key)));
    const dateList = dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    for (const date of dateList) {
      const clientFacingKPI = data[date].find(item => item.client_facing === true);
      if (clientFacingKPI) {
        defaultDate = date;
        ({ value } = clientFacingKPI);
        break;
      }
    }

    // Fallback: no client-facing run found (e.g. internal users viewing admin-only
    // runs) — default to the most recent date's first KPI so the page can render.
    if (value === undefined && dateList.length) {
      [defaultDate] = dateList;
      value = data[dateList[0]][0]?.value;
    }
  }

  return { date: defaultDate, kpi: value };
};

export const isBrandImpactDataComplete = (data: BrandImpactData): boolean =>
  data.weeklyBrandHealthTimeSeries.length > 0 &&
  Object.keys(data.modelOverview).length > 0 &&
  Object.keys(data.weeklyBrandHealthAreaChartSpend).length > 0 &&
  Object.keys(data.implicationsData).length > 0 &&
  Object.keys(data.paidMediaAreaChart).length > 0 &&
  Object.keys(data.paidMediaBarChart).length > 0 &&
  Object.keys(data.spendAndEffectBarChart).length > 0 &&
  Object.keys(data.nextBestDollarBarChart).length > 0 &&
  Object.keys(data.decayData).length > 0 &&
  Object.keys(data.saturationData).length > 0;

export const isBusinessImpactDataComplete = (data: BusinessImpactData): boolean =>
  data.weeklyBrandHealthTimeSeries.length > 0 &&
  Object.values(data.modelOverview).every(
    value => value !== null && value !== undefined && value !== ''
  ) &&
  data.weeklyRevenueTimeSeries.length > 0 &&
  data.totalRevenueDecomposition.length > 0 &&
  data.weeklyRevenueDecomposition.length > 0 &&
  data.revenueImpactTakeaways !== undefined &&
  data.weeklyIncrementalRoas.length > 0 &&
  data.incrementalRoasTakeaways !== undefined &&
  data.incrementalRoasTakeaways !== null;

export const formatModelOverviewOutcomeVariable = (outcomeVariable: string): string => {
  if (isQueryVolumeMetric(outcomeVariable)) {
    return 'GQV';
  }
  switch (outcomeVariable) {
    case 'total_revenue':
      return 'Revenue';
    default:
      return outcomeVariable === 'Awareness' ? 'Aided Awareness' : outcomeVariable;
  }
};

export const transformTakeawaysImpactRevenue = (
  incrementalRowsTakeawaysData: any[]
): RevenueImpactTakeaways => {
  const maxDate = incrementalRowsTakeawaysData.reduce((max, row) => {
    const rowDate = new Date(row.date);
    return rowDate > max ? rowDate : max;
  }, new Date(incrementalRowsTakeawaysData[0].date));

  const twelveMonthsAgo = new Date(maxDate);
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

  let brandEquityIncrementalRevenue = 0;
  let mediaDrivenBrandEquityIncrementalRevenue = 0;
  let ttmRevenue = 0;

  incrementalRowsTakeawaysData.forEach(row => {
    const rowDate = new Date(row.date);
    if (rowDate >= twelveMonthsAgo && rowDate <= maxDate) {
      if (row.channelGroup === 'Brand Effect') {
        brandEquityIncrementalRevenue += row.value || 0;
        ttmRevenue += row.response || 0;
      } else if (row.channelGroup === 'Spend Induced Brand Effect') {
        mediaDrivenBrandEquityIncrementalRevenue += row.value || 0;
      }
    }
  });

  const total = brandEquityIncrementalRevenue + mediaDrivenBrandEquityIncrementalRevenue;
  const brandEquityRevenuePercent =
    ttmRevenue !== 0
      ? ((brandEquityIncrementalRevenue + mediaDrivenBrandEquityIncrementalRevenue) / ttmRevenue) *
        100
      : 0;

  const revenueImpactTakeaways: RevenueImpactTakeaways = {
    name: 'Revenue Impact Summary',
    MediaDrivenBrandEquity: mediaDrivenBrandEquityIncrementalRevenue,
    BrandEquityRevenue: brandEquityIncrementalRevenue,
    Total: total,
    BrandEquityRevenuePercent: brandEquityRevenuePercent
  };

  return revenueImpactTakeaways;
};

export const groupChartDataByDate = (
  formattedGqvLineChartData: any[],
  groupingOption: DateGroupingKey,
  fiscalCal?: CompanyFiscalCal
): any[] => {
  const groups: Record<string, any> = {};
  const groupCount: Record<string, number> = {}; // Use for averages
  formattedGqvLineChartData.forEach((item: any) => {
    const groupKey = getGroupingValue(item.date, groupingOption, fiscalCal ?? {});
    groupCount[groupKey] = 1 + (groupCount[groupKey] ?? 0);
    if (!groups[groupKey]) {
      groups[groupKey] = { date: groupKey };
    }

    for (const [key, value] of Object.entries(item)) {
      if (key !== 'date' && typeof value === 'number') {
        groups[groupKey][key] = ((groups[groupKey][key] as number) || 0) + value;
      }
    }
  });

  for (const groupKey of Object.keys(groups)) {
    for (const [key, value] of Object.entries(groups[groupKey])) {
      if (isQueryVolumeMetric(key)) {
        groups[groupKey][key] = (Number(value) * 1.0) / groupCount[groupKey];
      }
    }
  }

  return Object.values(groups);
};

export const DATE_GROUPING_OPTIONS_BRAND_EQUITY: DateGroupingOption[] = [
  { value: 'Week', label: 'Weekly' },
  { value: 'Month', label: 'Monthly' },
  { value: 'Quarter', label: 'Quarterly' }
];
